import React, { useCallback, useEffect, useRef, useState } from "react";
import { logix2ReasoningApi, logix2HitlApi } from "@/api/oraclePhase28Client";
import { logix2Dock } from "@/api/adminOneClient";
import { usePersonalization } from "@/components/personalization/PersonalizationContext";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — LOGIX-2™ Intelligence Console (Phase 29A).
 *
 * Premium console combining:
 *   • Voice transcript pane (Web Speech API → Whisper fallback)
 *   • Image intelligence pane (vision LLM)
 *   • Multi-agent reasoning pane with HITL controls
 *   • Read-aloud synthesizer (browser TTS → ElevenLabs fallback)
 *
 * Strictly additive — must coexist with existing Logix2ReasoningPanel and
 * Logix2RecommendationCard, no replacement. Compliance lock applied to every
 * surface. HITL-only — no autonomous execution.
 */

const VERDICT_COLORS = {
  watch_strong: "#10b981",
  watch:        "#22c55e",
  consider:     "#fcd34d",
  hold:         "#94a3b8",
  avoid:        "#ef4444",
};

const STATUS_COLORS = {
  pending:    "#fcd34d",
  approved:   "#10b981",
  rejected:   "#ef4444",
  escalated:  "#a78bfa",
};

const BROWSER_STT_OK = typeof window !== "undefined" && (
  "webkitSpeechRecognition" in window || "SpeechRecognition" in window
);

const MEDIA_REC_OK = typeof window !== "undefined" && "MediaRecorder" in window && navigator?.mediaDevices?.getUserMedia;

function AgentBlock({ msg, testid }) {
  return (
    <article style={S.agent} data-testid={testid}>
      <header style={S.agentHead}>
        <span style={{ ...S.agentChip, color: msg.color, borderColor: msg.color, background: `${msg.color}1a` }}>
          {msg.label}
        </span>
        {msg.offline && (
          <span style={S.offlinePill} title={msg.error || "offline fallback"}>offline fallback</span>
        )}
        <time style={S.agentTime}>{(msg.timestamp || "").slice(11, 19)}</time>
      </header>
      <pre style={S.agentBody}>{msg.text}</pre>
    </article>
  );
}

export default function Logix2IntelligenceConsole({
  symbol = "AAPL",
  assetClass = "stocks",
  scannerType,
  isAdmin = false,
  testid = "logix2-intelligence-console",
}) {
  // Phase 31 — personalization context for user_id pass-through (advisory only)
  const _personalization = usePersonalization();
  const _userId = _personalization?.profile?.user_id || null;
  // Voice
  const [transcript, setTranscript] = useState("");
  const [micState, setMicState] = useState("idle"); // idle | recording | transcribing | error
  const [micErr, setMicErr] = useState("");
  const recogRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  // Image
  const fileRef = useRef(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageAnalysis, setImageAnalysis] = useState(null);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageErr, setImageErr] = useState("");
  const [pendingImageFile, setPendingImageFile] = useState(null);

  // Reasoning
  const [trace, setTrace] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [hitlBusy, setHitlBusy] = useState("");

  // Read aloud
  const [speaking, setSpeaking] = useState(false);

  // ----- Mic helpers -----
  const cleanupMic = useCallback(() => {
    try { recogRef.current && recogRef.current.stop(); } catch { /* ignore */ }
    try { recorderRef.current && recorderRef.current.state === "recording" && recorderRef.current.stop(); } catch { /* ignore */ }
    try { streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    recogRef.current = null;
    recorderRef.current = null;
    streamRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => () => cleanupMic(), [cleanupMic]);

  const startBrowserStt = useCallback(() => {
    try {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recog = new SR();
      recog.continuous = false;
      recog.interimResults = false;
      recog.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";
      recog.onstart = () => setMicState("recording");
      recog.onresult = (e) => {
        const text = (e?.results?.[0]?.[0]?.transcript || "").trim();
        if (text) setTranscript((prev) => (prev ? `${prev} ${text}` : text));
        setMicState("idle");
      };
      recog.onerror = (ev) => {
        setMicErr(ev?.error || "mic_error");
        setMicState("error");
      };
      recog.onend = () => { setMicState((s) => (s === "recording" ? "idle" : s)); };
      recog.start();
      recogRef.current = recog;
    } catch (e) {
      setMicErr(e?.message || "mic_start_failed");
      setMicState("error");
    }
  }, []);

  const startMediaRecorder = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      recorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstart = () => setMicState("recording");
      mr.onstop = async () => {
        setMicState("transcribing");
        const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
        const fd = new FormData();
        fd.append("audio", blob, "voice.webm");
        try {
          const { data } = await logix2Dock.voiceTranscribe(fd);
          if (data?.text) {
            setTranscript((prev) => (prev ? `${prev} ${data.text}` : data.text));
            setMicState("idle");
          } else {
            setMicErr(data?.reason || "transcription_unavailable");
            setMicState("error");
          }
        } catch (e) {
          setMicErr(e?.response?.data?.detail || e?.message || "transcribe_failed");
          setMicState("error");
        } finally {
          cleanupMic();
        }
      };
      mr.start();
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setMicErr("Microphone permission denied. Allow access to use voice input.");
      } else {
        setMicErr(e?.message || "mic_unavailable");
      }
      setMicState("error");
    }
  }, [cleanupMic]);

  const onMicClick = useCallback(() => {
    setMicErr("");
    if (micState === "recording") {
      try { recogRef.current && recogRef.current.stop(); } catch { /* ignore */ }
      try { recorderRef.current && recorderRef.current.state === "recording" && recorderRef.current.stop(); } catch { /* ignore */ }
      return;
    }
    if (BROWSER_STT_OK) {
      startBrowserStt();
    } else if (MEDIA_REC_OK) {
      startMediaRecorder();
    } else {
      setMicErr("Microphone not supported in this browser.");
      setMicState("error");
    }
  }, [micState, startBrowserStt, startMediaRecorder]);

  const clearTranscript = useCallback(() => setTranscript(""), []);

  // ----- Image upload -----
  const pickImage = () => {
    setImageErr("");
    try { fileRef.current && fileRef.current.click(); } catch { /* ignore */ }
  };

  const onImageChange = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setImageErr("Image too large (max 8 MB).");
      e.target.value = "";
      return;
    }
    setImageBusy(true);
    setImageAnalysis(null);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      setImagePreview(dataUrl);
      setPendingImageFile(file);
      // Eagerly call the vision endpoint so the user sees structured analysis.
      const fd = new FormData();
      fd.append("image", file);
      if (query) fd.append("prompt", query);
      const { data } = await logix2Dock.imageSearch(fd);
      setImageAnalysis(data);
    } catch (e2) {
      setImageErr(e2?.response?.data?.detail || e2?.message || "image_analysis_failed");
    } finally {
      setImageBusy(false);
      try { e.target.value = ""; } catch { /* ignore */ }
    }
  }, [query]);

  const clearImage = useCallback(() => {
    setImagePreview("");
    setImageAnalysis(null);
    setPendingImageFile(null);
    setImageErr("");
  }, []);

  // ----- Reasoning -----
  const runReasoning = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      let response;
      if (transcript || pendingImageFile) {
        const fd = new FormData();
        fd.append("symbol", symbol);
        fd.append("asset_class", assetClass);
        fd.append("user_query", query || "");
        if (scannerType) fd.append("scanner_type", scannerType);
        if (transcript) fd.append("voice_transcript", transcript);
        if (pendingImageFile) fd.append("image", pendingImageFile);
        if (_userId) fd.append("user_id", _userId);
        response = await logix2ReasoningApi.reasonWithAttachments(fd);
      } else if (_userId) {
        // Phase 31 — fold user_id into the multipart route so reasoning can
        // be personalized even without attachments.
        const fd = new FormData();
        fd.append("symbol", symbol);
        fd.append("asset_class", assetClass);
        fd.append("user_query", query || "");
        if (scannerType) fd.append("scanner_type", scannerType);
        fd.append("user_id", _userId);
        response = await logix2ReasoningApi.reasonWithAttachments(fd);
      } else {
        response = await logix2ReasoningApi.reason({
          symbol,
          asset_class: assetClass,
          user_query: query,
          scanner_type: scannerType,
        });
      }
      setTrace(response.data);
    } catch (e) {
      // 502 Fix Build Pack — never show raw "Request failed with status code 502".
      // Render a structured local fallback trace instead so the panel stays alive.
      const status = e?.response?.status;
      const debugId = e?.response?.data?.debug_id || (Math.random().toString(36).slice(2, 14));
      const isNetworkOrGateway = !status || status >= 502 || e?.code === "ECONNABORTED";
      if (isNetworkOrGateway) {
        setTrace({
          trace_id: `local-${debugId}`,
          debug_id: debugId,
          symbol,
          asset_class: assetClass,
          provider_status: "unavailable",
          status: "fallback",
          ok: true,
          verdict: "consider",
          agents: [
            {
              agent: "synthesizer",
              label: "Local Safety Layer",
              color: "#c5a059",
              offline: true,
              text:
                "LOGIX-2 premium reasoning provider is temporarily unavailable, but the local ORACLE safety layer is still active. " +
                "Here is a structured preliminary analysis: cross-check the latest fundamentals and any recent news catalysts, " +
                "verify liquidity and spread before sizing, and define entry, stop, and exit before considering any action. " +
                "Human-in-the-loop confirmation is required before acting.",
              timestamp: new Date().toISOString(),
            },
          ],
          compliance:
            "Educational research only. Not financial advice. Not betting advice. No guarantees. Human confirmation required.",
        });
        setErr("");
      } else {
        setErr(e?.response?.data?.detail || e?.message || "reason_failed");
      }
    } finally { setBusy(false); }
  }, [symbol, assetClass, query, scannerType, transcript, pendingImageFile, _userId]);

  // Auto-kick a baseline trace on mount so the panel is not empty.
  const _autoKickedRef = useRef(false);
  useEffect(() => {
    if (_autoKickedRef.current) return;
    _autoKickedRef.current = true;
    // Defer to next tick so we don't set state during the initial render cycle.
    const t = setTimeout(() => { runReasoning(); }, 0);
    return () => clearTimeout(t);
  }, [runReasoning]);

  const runHitl = useCallback(async (action) => {
    if (!trace?.trace_id || !isAdmin) return;
    setHitlBusy(action);
    try {
      const op = logix2HitlApi[action];
      if (!op) return;
      const { data } = await op(trace.trace_id, "");
      setTrace((t) => ({ ...t, ...data }));
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || `${action}_failed`);
    } finally { setHitlBusy(""); }
  }, [trace, isAdmin]);

  // ----- Read aloud -----
  const readAloud = useCallback(async () => {
    if (!trace) return;
    setSpeaking(true);
    try {
      // Use Synthesizer text if available, else first agent text.
      const synth = (trace.agents || []).find((a) => a.agent === "synthesizer") || (trace.agents || [])[0];
      const text = (synth?.text || "").slice(0, 1200);
      if (!text) return;
      try {
        const { data } = await logix2Dock.voiceSpeak({ text, provider: "browser", summary_only: true });
        const spoken = data?.spoken_text || data?.text || text;
        if (data?.provider === "elevenlabs" && data?.audio_b64) {
          const audio = new Audio(`data:${data.audio_mime || "audio/mpeg"};base64,${data.audio_b64}`);
          audio.onended = () => setSpeaking(false);
          audio.play();
          return;
        }
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
          const u = new SpeechSynthesisUtterance(spoken);
          u.rate = data?.rate || 1.0;
          u.pitch = data?.pitch || 1.0;
          u.onend = () => setSpeaking(false);
          window.speechSynthesis.cancel();
          window.speechSynthesis.speak(u);
          return;
        }
        setSpeaking(false);
      } catch (e) {
        // Fallback to direct browser TTS without server sanitization.
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
          const u = new SpeechSynthesisUtterance(text);
          u.onend = () => setSpeaking(false);
          window.speechSynthesis.cancel();
          window.speechSynthesis.speak(u);
        } else {
          setSpeaking(false);
        }
      }
    } finally {
      // safety net: clear after 60s
      setTimeout(() => setSpeaking(false), 60_000);
    }
  }, [trace]);

  const stopSpeaking = useCallback(() => {
    try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch { /* ignore */ }
    setSpeaking(false);
  }, []);

  const verdictColor = VERDICT_COLORS[trace?.verdict] || VERDICT_COLORS.consider;
  const statusColor = STATUS_COLORS[trace?.status] || STATUS_COLORS.pending;

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <span style={S.tagline}>LOGIX-2™ Intelligence Console · Voice · Vision · Multi-Agent</span>
          <h3 style={S.title}>
            Premium reasoning for <code>{symbol}</code>{" "}
            <small style={{ color: "#94a3b8" }}>{assetClass}</small>
          </h3>
        </div>
        <div style={S.headerActions}>
          {trace?.verdict && (
            <span style={{ ...S.pill, color: verdictColor, borderColor: verdictColor, background: `${verdictColor}1a` }} data-testid={`${testid}-verdict`}>
              {trace.verdict.toUpperCase().replace(/_/g, " ")}
            </span>
          )}
          {trace?.status && (
            <span style={{ ...S.pill, color: statusColor, borderColor: statusColor, background: `${statusColor}1a` }} data-testid={`${testid}-status`}>
              {trace.status.toUpperCase()}
            </span>
          )}
        </div>
      </header>

      {/* Voice + Image controls row */}
      <div style={S.toolRow}>
        <button
          type="button"
          onClick={onMicClick}
          disabled={micState === "transcribing"}
          style={{
            ...S.micBtn,
            color: micState === "recording" ? "#ef4444" : "#c5a059",
            borderColor: micState === "recording" ? "#ef4444" : "rgba(197,160,89,0.55)",
            background: micState === "recording" ? "rgba(239,68,68,0.10)" : "rgba(197,160,89,0.08)",
          }}
          data-testid={`${testid}-mic`}
        >
          {micState === "transcribing" ? "…" : micState === "recording" ? "● Recording — tap to stop" : "🎙 Push-to-talk"}
        </button>
        <button type="button" onClick={pickImage} disabled={imageBusy} style={S.imageBtn} data-testid={`${testid}-image-pick`}>
          {imageBusy ? "Analyzing image…" : "📷 Upload chart / screenshot"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
          onChange={onImageChange}
          style={{ display: "none" }}
          data-testid={`${testid}-image-input`}
        />
        {speaking ? (
          <button type="button" onClick={stopSpeaking} style={S.stopSpeakBtn} data-testid={`${testid}-stop-speak`}>
            ⏹ Stop voice
          </button>
        ) : (
          <button type="button" onClick={readAloud} disabled={!trace} style={S.speakBtn} data-testid={`${testid}-read-aloud`}>
            🔊 Read aloud
          </button>
        )}
      </div>

      {/* Voice transcript pane */}
      <div style={S.pane} data-testid={`${testid}-voice-pane`}>
        <header style={S.paneHead}>
          <strong style={S.paneTitle}>Voice transcript</strong>
          {transcript && (
            <button type="button" onClick={clearTranscript} style={S.clearLink} data-testid={`${testid}-clear-voice`}>
              clear
            </button>
          )}
        </header>
        {micErr && <div style={S.warn}>⚠ {micErr}</div>}
        <textarea
          rows={2}
          placeholder="Push-to-talk or type a question for LOGIX-2 here. The transcript is folded into the multi-agent reasoning context."
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          style={S.transcriptArea}
          data-testid={`${testid}-transcript`}
        />
      </div>

      {/* Image intelligence pane */}
      {(imagePreview || imageAnalysis || imageErr) && (
        <div style={S.pane} data-testid={`${testid}-image-pane`}>
          <header style={S.paneHead}>
            <strong style={S.paneTitle}>Image intelligence</strong>
            {(imagePreview || imageAnalysis) && (
              <button type="button" onClick={clearImage} style={S.clearLink} data-testid={`${testid}-clear-image`}>
                clear
              </button>
            )}
          </header>
          {imageErr && <div style={S.warn}>⚠ {imageErr}</div>}
          <div style={S.imageRow}>
            {imagePreview && (
              <img src={imagePreview} alt="uploaded preview" style={S.imagePreview} data-testid={`${testid}-image-preview`} />
            )}
            <div style={S.imageMeta}>
              {imageBusy && <div style={S.imageHint}>Analyzing image with LOGIX-2 vision…</div>}
              {imageAnalysis && imageAnalysis.ok && (
                <>
                  <div style={S.imageBlock}><strong style={S.imageLbl}>Visible:</strong> {imageAnalysis.visible}</div>
                  {imageAnalysis.extracted_context && (
                    <div style={S.imageBlock}><strong style={S.imageLbl}>Context:</strong> {imageAnalysis.extracted_context}</div>
                  )}
                  {Array.isArray(imageAnalysis.key_data_points) && imageAnalysis.key_data_points.length > 0 && (
                    <div style={S.imageBlock}>
                      <strong style={S.imageLbl}>Data points:</strong>
                      <ul style={S.imageList}>
                        {imageAnalysis.key_data_points.slice(0, 6).map((d, i) => <li key={i}>{d}</li>)}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(imageAnalysis.risks) && imageAnalysis.risks.length > 0 && (
                    <div style={S.imageBlock}>
                      <strong style={S.imageLbl}>Risks:</strong>
                      <ul style={S.imageList}>
                        {imageAnalysis.risks.slice(0, 4).map((d, i) => <li key={i}>{d}</li>)}
                      </ul>
                    </div>
                  )}
                </>
              )}
              {imageAnalysis && !imageAnalysis.ok && (
                <div style={S.imageHint}>
                  Image received. Vision pipeline returned: <code>{imageAnalysis.error || "unavailable"}</code>.
                  Reasoning will still run with attachment metadata.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Query + Run */}
      <div style={S.queryRow}>
        <input
          type="text"
          placeholder="Optional written question (added to the multi-agent prompt)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={S.queryInput}
          data-testid={`${testid}-query`}
        />
        <button type="button" onClick={runReasoning} disabled={busy} style={S.primaryBtn} data-testid={`${testid}-run`}>
          {busy ? "Thinking…" : "Run LOGIX-2"}
        </button>
      </div>

      {err && <div style={S.err} data-testid={`${testid}-error`}>⚠ {err}</div>}

      {/* 502 Fix Build Pack — premium-styled fallback card */}
      {trace?.provider_status && trace.provider_status !== "live" && (
        <div style={S.fallbackCard} data-testid={`${testid}-fallback-card`}>
          <div style={S.fallbackTitle}>
            <span style={S.fallbackBadge} data-testid={`${testid}-fallback-badge`}>
              FALLBACK MODE · SAFE RESPONSE
            </span>
            <strong style={{ color: "#fde68a" }}>
              {trace.provider_status === "unavailable"
                ? "LOGIX-2 Provider Temporarily Unavailable"
                : "Local Safety Layer Active"}
            </strong>
          </div>
          <p style={S.fallbackBody}>
            Voice is active. Local ORACLE safety analysis is still available. Premium
            multi-agent reasoning could not complete.
          </p>
          <div style={S.fallbackActions}>
            <button type="button" onClick={runReasoning} disabled={busy} style={S.retryBtn} data-testid={`${testid}-retry`}>
              ↻ Retry LOGIX-2
            </button>
            <button
              type="button"
              onClick={() => { try { navigator.clipboard.writeText(trace.debug_id || trace.trace_id || ""); } catch { /* ignore */ } }}
              style={S.copyDebugBtn}
              data-testid={`${testid}-copy-debug`}
            >
              Copy Debug ID
            </button>
            <span style={S.debugCode}>{(trace.debug_id || trace.trace_id || "").slice(0, 12)}</span>
          </div>
        </div>
      )}

      {/* Multi-agent trace */}
      {trace?.agents?.length > 0 && (
        <div style={S.agents} data-testid={`${testid}-agents`}>
          {trace.agents.map((m) => (
            <AgentBlock key={`${trace.trace_id}-${m.agent}`} msg={m} testid={`${testid}-agent-${m.agent}`} />
          ))}
        </div>
      )}

      {/* HITL actions */}
      {isAdmin && trace?.trace_id && (
        <div style={S.hitlRow} data-testid={`${testid}-hitl`}>
          <button type="button" disabled={hitlBusy !== "" || trace.status === "approved"} onClick={() => runHitl("approve")} style={S.approveBtn} data-testid={`${testid}-approve`}>
            ✓ Approve
          </button>
          <button type="button" disabled={hitlBusy !== "" || trace.status === "rejected"} onClick={() => runHitl("reject")} style={S.rejectBtn} data-testid={`${testid}-reject`}>
            ✕ Reject
          </button>
          <button type="button" disabled={hitlBusy !== "" || trace.status === "escalated"} onClick={() => runHitl("escalate")} style={S.escalateBtn} data-testid={`${testid}-escalate`}>
            ⚑ Escalate
          </button>
          <span style={S.traceCode} title="Trace ID">{trace.trace_id.slice(0, 12)}…</span>
        </div>
      )}

      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance`} />
    </section>
  );
}

const S = {
  shell: { background: "linear-gradient(135deg, rgba(11,15,20,0.96), rgba(8,32,22,0.95))", border: "1px solid rgba(197,160,89,0.30)", borderRadius: 14, padding: 14, color: "#e2e8f0", display: "flex", flexDirection: "column", gap: 12, fontFamily: "'Figtree', 'Inter', system-ui, sans-serif", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  tagline: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: "#fde68a", margin: 0, fontSize: 16 },
  headerActions: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  pill: { fontFamily: "'Space Mono', monospace", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.10em", padding: "4px 10px", border: "1px solid", borderRadius: 999 },
  toolRow: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
  micBtn: { appearance: "none", border: "1px solid", padding: "8px 13px", borderRadius: 10, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em", minWidth: 200 },
  imageBtn: { appearance: "none", background: "rgba(34,211,238,0.10)", border: "1px solid #22d3ee", color: "#22d3ee", padding: "8px 13px", borderRadius: 10, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em" },
  speakBtn: { appearance: "none", background: "rgba(167,139,250,0.10)", border: "1px solid #a78bfa", color: "#a78bfa", padding: "8px 13px", borderRadius: 10, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em" },
  stopSpeakBtn: { appearance: "none", background: "rgba(239,68,68,0.10)", border: "1px solid #ef4444", color: "#ef4444", padding: "8px 13px", borderRadius: 10, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em" },
  pane: { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  paneHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  paneTitle: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.12em" },
  clearLink: { appearance: "none", background: "transparent", border: "none", color: "#94a3b8", textDecoration: "underline", cursor: "pointer", fontSize: 10.5, fontFamily: "'Space Mono', monospace" },
  warn: { padding: "5px 9px", background: "rgba(252,211,77,0.10)", border: "1px solid rgba(252,211,77,0.30)", color: "#fde68a", borderRadius: 8, fontSize: 11.5 },
  transcriptArea: { width: "100%", appearance: "none", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.10)", color: "#e2e8f0", padding: "8px 10px", borderRadius: 8, fontSize: 12.5, fontFamily: "'Figtree', system-ui, sans-serif", resize: "vertical", boxSizing: "border-box" },
  imageRow: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-start" },
  imagePreview: { maxWidth: 220, maxHeight: 180, borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)", objectFit: "contain", background: "#000" },
  imageMeta: { flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 6, color: "#cbd5e1", fontSize: 12, lineHeight: 1.5 },
  imageBlock: { color: "#cbd5e1", fontSize: 12, lineHeight: 1.5 },
  imageLbl: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.06em", marginRight: 6 },
  imageList: { margin: "4px 0 0 16px", padding: 0 },
  imageHint: { color: "#94a3b8", fontSize: 12, fontStyle: "italic" },
  queryRow: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  queryInput: { flex: 1, minWidth: 180, appearance: "none", background: "#040608", border: "1px solid rgba(255,255,255,0.10)", color: "#e2e8f0", padding: "9px 11px", borderRadius: 10, fontSize: 12.5, fontFamily: "'Figtree', system-ui, sans-serif" },
  primaryBtn: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "9px 16px", borderRadius: 10, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em" },
  err: { padding: "8px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#fecaca", borderRadius: 10, fontSize: 12 },
  agents: { display: "flex", flexDirection: "column", gap: 8 },
  agent: { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 6 },
  agentHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  agentChip: { fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.08em", fontWeight: 800, padding: "3px 9px", border: "1px solid", borderRadius: 999 },
  offlinePill: { fontSize: 9.5, color: "#fcd34d", fontFamily: "'Space Mono', monospace", border: "1px solid #fcd34d55", borderRadius: 999, padding: "1px 7px", letterSpacing: "0.05em" },
  agentTime: { color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 10 },
  agentBody: { margin: 0, color: "#cbd5e1", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif", fontSize: 12.5, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" },
  hitlRow: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
  approveBtn: { appearance: "none", background: "rgba(16,185,129,0.10)", border: "1px solid #10b981", color: "#10b981", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  rejectBtn: { appearance: "none", background: "rgba(239,68,68,0.10)", border: "1px solid #ef4444", color: "#ef4444", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  escalateBtn: { appearance: "none", background: "rgba(167,139,250,0.10)", border: "1px solid #a78bfa", color: "#a78bfa", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  traceCode: { color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 10, marginLeft: 4 },
  fallbackCard: { background: "linear-gradient(135deg, rgba(252,211,77,0.06), rgba(167,139,250,0.04))", border: "1px solid rgba(252,211,77,0.35)", borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 8 },
  fallbackTitle: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  fallbackBadge: { fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.14em", color: "#fde68a", background: "rgba(252,211,77,0.16)", border: "1px solid rgba(252,211,77,0.55)", padding: "3px 9px", borderRadius: 999, fontWeight: 800 },
  fallbackBody: { color: "#cbd5e1", fontSize: 12.5, lineHeight: 1.5, margin: 0 },
  fallbackActions: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
  retryBtn: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "7px 13px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em" },
  copyDebugBtn: { appearance: "none", background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#cbd5e1", padding: "7px 13px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  debugCode: { color: "#94a3b8", fontFamily: "'Space Mono', monospace", fontSize: 10.5 },
};
